import { HttpClient } from '@angular/common/http';
import { Injectable, isDevMode } from '@angular/core';
import { DataStateChangeEventArgs } from '@syncfusion/ej2-angular-grids';
import { Observable, throwError, Subject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpHelper } from '../helpers/http-helper';
import { IEntity, ILLRevisionHistory, IServerSettings } from '../models/index';
import { AuthenticationService } from './authentication.service';
import { SettingsService } from './settings.service';
import { StorageManagerService } from './storage-manager.service';

@Injectable()
export class ERFormService extends Subject<DataStateChangeEventArgs> {

  public readonly ER_SPL_NO_CHANGE_NOTIFICATION = 889;
  public readonly ER_SPL_OUT_OF_BUSINESS_NOTIFICATION = 890;

  private action: string;
  private settings: IServerSettings;
  private apiUrl: string;
  apiResult: any;
  httpHelper: HttpHelper;

  constructor (private http: HttpClient, private settingsService: SettingsService, private authService: AuthenticationService, private storageManagerService: StorageManagerService) {
    super();

    // Define an instance of the http helper class that will be used to create httpHeader for api calls
    this.httpHelper = new HttpHelper(http, authService, storageManagerService);
  }

  // Get a list of ER forms for the dashboard
  getERForms(): Observable<any[]> {

    const headers = this.httpHelper.createHttpHeaders();
    this.settings = this.settingsService.getSettings();
    this.apiUrl = this.settings.ApiGatewayUrl;
    this.action = 'ssdpds/v1/ERForms/ERDashboard';

    return this.http.get(`${ this.apiUrl }/${ this.action }`, { headers: headers })
      .pipe(map(this.extractData),
        catchError(this.handleErrors));
  }

  // Get ER form data by revisionid
  getERFormById(revisionId: string): Observable<IEntity[]> {

    const headers = this.httpHelper.createHttpHeaders();
    this.settings = this.settingsService.getSettings();
    this.apiUrl = this.settings.ApiGatewayUrl;
    this.action = `ssdpds/v1/ERForms/${ revisionId }`;

    return this.http.get(`${ this.apiUrl }/${ this.action }`, { headers: headers })
      .pipe(map(this.extractData),
        catchError(this.handleErrors));
  }

  getRegistrantCompanyContactInfo(revisionId: string): Observable<IEntity[]> {

    const headers = this.httpHelper.createHttpHeaders();
    this.settings = this.settingsService.getSettings();
    this.apiUrl = this.settings.ApiGatewayUrl;
    this.action = `ssdpds/v1/LLForms/${ revisionId }`;

    return this.http.get(`${ this.apiUrl }/${ this.action }`, { headers: headers })
      .pipe(map(this.extractData),
        catchError(this.handleErrors));
  }

  // Create a draft version of a new ER form
  createERForm(formValues) {

    const headers = this.httpHelper.createHttpHeaders();
    this.settings = this.settingsService.getSettings();
    this.apiUrl = this.settings.ApiGatewayUrl;
    this.action = 'ssdpds/v1/ERForms';

    return this.http.post(`${ this.apiUrl }/${ this.action }`, formValues, { headers: headers })
      .pipe(map(this.extractData),
        catchError(this.handleErrors));
  }

  // Get ssdp vacabularies
  getSSDPVocabularies(): Observable<IEntity[]> {

    const headers = this.httpHelper.createHttpHeaders();
    this.settings = this.settingsService.getSettings();
    this.apiUrl = this.settings.ApiGatewayUrl;
    this.action = 'ssdpds/v1/CommonData/Vocabularies';

    return this.http.get(`${ this.apiUrl }/${ this.action }`, { headers: headers })
      .pipe(map(this.extractData),
        catchError(this.handleErrors));
  }

  // Get revision history
  getRevisionHistory(revisionId: number): Observable<ILLRevisionHistory[]> {

    const headers = this.httpHelper.createHttpHeaders();
    this.settings = this.settingsService.getSettings();
    this.apiUrl = this.settings.ApiGatewayUrl;
    this.action = `ssdpds/v1/ERForms/ERVersionHistory/${ revisionId }`;

    return this.http.get(`${ this.apiUrl }/${ this.action }`, { headers: headers })
      .pipe(map(this.extractData),
        catchError(this.handleErrors));
  }

  // Save ER Form as a draft
  saveERForm(erData, modifiedByUser: string, draftMode: boolean, isIngestion: boolean) {

    let headers = this.httpHelper.createHttpHeaders();
    this.settings = this.settingsService.getSettings();
    this.apiUrl = this.settings.ApiGatewayUrl;
    this.action = `ssdpds/v1/ERForms?isDraftMode=${ draftMode }&isIngestion=${ isIngestion }`;

    headers = headers.set('modifiedByUser', modifiedByUser);

    return this.http.post(`${ this.apiUrl }/${ this.action }`, erData, { headers: headers })
      .pipe(map(this.extractData),
        catchError(this.handleErrors));
  }

    buildERspl(erData: any, modifiedByUser: string, ignoreWarnings: boolean, newSPLVersion: number) {

    let headers = this.httpHelper.createHttpHeaders();
    this.settings = this.settingsService.getSettings();
    this.apiUrl = this.settings.ApiGatewayUrl;
    this.action = `ssdpds/v1/ERForms/Build?ignoreWarnings=${ignoreWarnings}&newSplVersionNumber=${newSPLVersion}`;

    headers = headers.set('modifiedByUser', modifiedByUser);

    return this.http.post(`${ this.apiUrl }/${ this.action }`, erData, { headers: headers })
      .pipe(map(this.extractData),
        catchError(this.handleErrors));
  }

  validateERform(erData: any, erRevisionId: number, modifiedByUser: string, hasChanges: boolean, ignoreWarnings: boolean) {

    let headers = this.httpHelper.createHttpHeaders();
    this.settings = this.settingsService.getSettings();
    this.apiUrl = this.settings.ApiGatewayUrl;
    this.action = `ssdpds/v1/ERForms/Validate?hasChanges=${hasChanges}&erRevisionId=${erRevisionId ? erRevisionId : 0}&ignoreWarnings=${ignoreWarnings}`;

    headers = headers.set('modifiedByUser', modifiedByUser);

    return this.http.post(`${this.apiUrl}/${this.action}`, erData, { headers: headers })
        .pipe(map(this.extractData),
          catchError(this.handleErrors));
  }

   // Get a list of ER deliveries for the dashboard
   getERDelilveries(): Observable<any[]> {
    const headers = this.httpHelper.createHttpHeaders();
    this.settings = this.settingsService.getSettings();
    this.apiUrl = this.settings.ApiGatewayUrl;
    this.action = 'ssdpds/v1/ERForms/ERDeliveryDashboard';

    return this.http.get(`${this.apiUrl}/${this.action}`, { headers: headers })
        .pipe(map(this.extractData),
          catchError(this.handleErrors));
  }

  // Get data successfully from api call
  private extractData(response: Response) {
    if (response.status < 200 || response.status >= 300) {
      throw new Error('Bad response status: ' + response.status);
    }

    const body = response;
    return body || {};
  }

  // Error handling
  private handleErrors(error: any): Observable<any> {
    const GENERIC_ERROR_MSG = 'It looks like something went wrong. Try reloading the page or contacting support if this issue continues.';
    const bDisplayDetailMsg = error && error.error && error.error.isBusinessErrorMessage ? true : isDevMode() ? true : false;
    error.error = error && error.error && error.error.message && bDisplayDetailMsg ? error.error.message : GENERIC_ERROR_MSG;

    return throwError(error.error);
  }
}
